import { AlertTriangle } from 'lucide-react'
import { type ReactNode, useCallback, useRef, useState } from 'react'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog'
import { cn } from '@/lib/utils'

export interface ConfirmOptions {
  title: string
  description?: ReactNode
  confirmText?: string
  cancelText?: string
  destructive?: boolean
}

/**
 * Imperative confirm dialog. Returns `confirm(options)` (resolves to a boolean)
 * and the `dialog` element to render in the component's tree.
 */
export function useConfirm() {
  const [open, setOpen] = useState(false)
  const [options, setOptions] = useState<ConfirmOptions | null>(null)
  const resolver = useRef<((value: boolean) => void) | null>(null)

  const confirm = useCallback((opts: ConfirmOptions) => {
    setOptions(opts)
    setOpen(true)
    return new Promise<boolean>((resolve) => {
      resolver.current = resolve
    })
  }, [])

  const close = (result: boolean) => {
    setOpen(false)
    resolver.current?.(result)
    resolver.current = null
  }

  const dialog = (
    <Dialog open={open} onOpenChange={(next) => !next && close(false)}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <div className="flex items-start gap-3 pr-6">
            <span
              className={cn(
                'flex h-10 w-10 shrink-0 items-center justify-center rounded-xl',
                options?.destructive
                  ? 'bg-destructive/10 text-destructive'
                  : 'bg-primary/10 text-primary'
              )}
            >
              <AlertTriangle className="h-5 w-5" />
            </span>
            <div className="flex flex-col gap-1">
              <DialogTitle>{options?.title}</DialogTitle>
              {options?.description && (
                <DialogDescription>{options.description}</DialogDescription>
              )}
            </div>
          </div>
        </DialogHeader>

        <DialogFooter>
          <Button variant="ghostDark" onClick={() => close(false)}>
            {options?.cancelText ?? 'Zrušiť'}
          </Button>
          <Button
            onClick={() => close(true)}
            className={cn(
              options?.destructive && 'bg-destructive text-white hover:bg-destructive-strong'
            )}
          >
            {options?.confirmText ?? 'Potvrdiť'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )

  return { confirm, dialog }
}
